import { NgTemplateOutlet, CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { ExpandModalDialog } from 'app/modules/well-integrity-management/component/expand-modal/expand-modal.component';
import { OtherActivitiesResponse } from '../../model/well-integrity-management.type';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'activities',
    templateUrl: './activities.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgTemplateOutlet, CommonModule],
    styleUrls: ['./activities.component.scss'],
})
export class ActivitiesComponent implements OnChanges {
    @Input() expandIcon: any;
    @Input() data: OtherActivitiesResponse;

    constructor(
        private readonly dialog: MatDialog,
        private readonly elementRef: ElementRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && this.data?.data?.activities) {
            this.createProgressBarChart();
            this.cdr.markForCheck();
        }
    }

    openDialog(title: string, componentName: string, functionName: string) {
        const component = document.getElementById(componentName);

        const dialogRef = this.dialog.open(ExpandModalDialog, {
            data: {
                title: title,
                viewComponent: component,
                functionName: functionName,
                apiData: this.data,
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                console.log(`Dialog result: ${result}`);
            });
    }

    createProgressBarChart() {
        const myGraphElement = document.getElementById('progressBarChart');
        if (!myGraphElement) return;

        myGraphElement.style.height = '45vh';
        myGraphElement.style.width = '100%';

        // Create chart instance
        const chart = am4core.create('progressBarChart', am4charts.XYChart);

        // Transform API data for the chart
        const activities = this.data?.data?.activities || [];
        chart.data = activities.map((activity) => ({
            activity: activity.activityName,
            completed: activity.completePercentage,
            notCompleted: activity.notCompletePercentage,
        }));

        // Create axes
        const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'activity';
        categoryAxis.renderer.grid.template.opacity = 0;
        categoryAxis.renderer.labels.template.fill = am4core.color('#000');

        const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.renderer.grid.template.opacity = 0;
        valueAxis.renderer.ticks.template.strokeOpacity = 0.5;
        valueAxis.renderer.ticks.template.stroke = am4core.color('#495C43');
        valueAxis.renderer.ticks.template.length = 10;
        valueAxis.renderer.line.strokeOpacity = 0.5;
        valueAxis.renderer.baseGrid.disabled = true;
        valueAxis.renderer.minGridDistance = 40;
        // valueAxis.renderer.labels.template.fill = am4core.color("#000");

        // Create series
        const createSeries = (field: string, name: string, color: string) => {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueX = field;
            series.dataFields.categoryY = 'activity';
            series.stacked = true;
            series.name = name;
            series.columns.template.height = am4core.percent(40);
            series.fill = am4core.color(color);
            series.strokeWidth = 2;
            series.stroke = am4core.color('#ffff');

            const labelBullet = series.bullets.push(
                new am4charts.LabelBullet(),
            );
            labelBullet.locationX = 0.5;
            labelBullet.label.text = '{valueX}';
            labelBullet.label.fill = am4core.color('#fff');
        };

        createSeries('completed', 'Complete', '#008FD3');
        createSeries('notCompleted', 'Not Complete', '#273367');

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.contentAlign = 'center';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;

        // Custom marker style
        chart.legend.markers.template.width = 12;
        chart.legend.markers.template.height = 12;
        let markerTemplate = chart.legend.markers.template.children.getIndex(
            0,
        ) as am4core.RoundedRectangle;
        markerTemplate.cornerRadius(4, 4, 4, 4);

        removeAmChartLogo(this.elementRef.nativeElement);
    }
}
